import { useCallback, useEffect, useState } from 'react';

import {
    handleEvent,
    JazzRoomEventAddTrack,
    JazzRoomEventTrackMuteChanged,
    JazzRoomMediaSourceState,
    JazzRoomParticipantId,
    JazzRoomVideoSource,
} from '@salutejs/jazz-sdk-web';
import { useRoomContext } from '../../contexts/RoomContext';

type VideoSourcesType = {
    primary: JazzRoomVideoSource;
    secondary?: JazzRoomVideoSource;
};
export const useVideoSources = (participantId?: JazzRoomParticipantId): VideoSourcesType => {
    const { room } = useRoomContext();
    const [state, setState] = useState<VideoSourcesType>({ primary: 'user' });

    const updateVideoSources = useCallback(({ payload }: JazzRoomEventAddTrack | JazzRoomEventTrackMuteChanged) => {
        if (payload.participantId !== participantId) return;

        const { mediaType } = payload;
        if (mediaType === 'displayScreen' || mediaType === 'video') {
            const displayScreenSource = room.getParticipantSource(participantId, 'displayScreen');
            const videoSource = room.getParticipantSource(participantId, 'video');
            setState(getVideoSources(videoSource, displayScreenSource));
        }
    }, [participantId]);

    useEffect(() => {
        if (!participantId) return;

        const displayScreenSource = room.getParticipantSource(participantId, 'displayScreen',);
        const videoSource = room.getParticipantSource(participantId, 'video');
        setState(getVideoSources(videoSource, displayScreenSource));

        const addTrackUnsubscribe = handleEvent(room.event$, 'addTrack', updateVideoSources);
        const muteChangeUnsubscribe = handleEvent(room.event$, 'trackMuteChanged', updateVideoSources);

        return () => {
            addTrackUnsubscribe();
            muteChangeUnsubscribe();
        };
    }, [room, participantId]);

    return state;
}

type MediaSourcesStateType = JazzRoomMediaSourceState | undefined;

const getVideoSources = (videoSource: MediaSourcesStateType, displayScreenSource: MediaSourcesStateType): VideoSourcesType => {
    if (!displayScreenSource || displayScreenSource.isMuted) {
        return {
            primary: 'user'
        };
    }
    if (videoSource && !videoSource.isMuted) {
        return {
            primary: 'display',
            secondary: 'user'
        }
    } else {
        return {
            primary: 'display'
        }
    }
}
